import {first} from 'rxjs/operators';
import { Component, Input, Output, EventEmitter, OnInit } from "@angular/core";
import { Router } from "@angular/router";

import { FlowTemplate, State, Transition, Section, Page, StateType } from "../../../common/models/flow.dto";
import { FlowTemplateService } from "../../services/flow-template.service";
import { SidePanelBase } from "../../../common/components/side-panel-base.class";
import { SidePanelService } from "../../../common/services/side-panel.service";

@Component({
    selector: "workflow-diagram-form-editor",
    templateUrl: "./workflow-diagram-form-editor.component.html",
    standalone: false
})

export class WorkflowDiagramFormEditorComponent extends SidePanelBase implements OnInit {
    static componentName = "WorkflowDiagramFormEditorComponent";


    public _originalStateName: string;
    _selectedState: State;


    activeTemplate: FlowTemplate;
    saveTableOptions = [{ id: 1, text: "Table 1" }, { id: 2, text: "Table 2" }];
    showGotoEditorUI: boolean = false;
    showTransitionEditor: boolean = false;
    showDuplicateNameWarning: boolean = false;
    requireSave: boolean = false;
    preEditStateName: string;
    formType: string = "";
    moduleGroupId: string | null = null;
    moduleId: string | null = null;

    get formHeading(): string {
        switch (this.formType) {
            case StateType.Start:
                return "Start";
            case StateType.End:
                return "End";
            case StateType.InputForm:
                return "Input Form";
            case StateType.ReviewForm:
                return "Review Form";
            default:
                return "";
        }
    }

    get disableForStart(): boolean {
        return this.selectedState.isInitial && this.selectedState.transitions.length > 0;
    }

    get disableRedoForStart(): boolean {
        return this.selectedState.isInitial;
    }

    get disableReviewForm(): boolean {
        return false; // Sequential review forms allowed

        // Sequential review forms prevented, leaving in for now
        // return this.selectedState.stateType === StateType.ReviewForm;
    }

    get disableReviewLevel(): boolean {
        return this.selectedState.stateType !== StateType.ReviewForm;
    }

    get availableStates(): Array<State> {
        //catch for test
        if (this.activeTemplate == undefined || this.activeTemplate.states.length === 0) {
            return [{ key: 1, name: "", stateType: StateType.Undefined, pages: [] } as State];
        } else {
            //return this.activeTemplate.states;
            const states = new Array<State>();
            this.activeTemplate.states.forEach(s => {
                if (s.key !== this.selectedState.key) {
                    states.push(s);
                }
            });
            return states;
        }
    }

    get selectedState(): State {
        if (this._selectedState === undefined || this._selectedState === null) {
            this._selectedState = {
                transitions: new Array<Transition>(),
                sections: new Array<Section>(),
                stateType: StateType.Undefined
            };
        }
        return this._selectedState;
    }

    constructor(public flowTemplateService: FlowTemplateService, public router: Router, public sidePanelService: SidePanelService) {
        super();
    }

    ngOnInit() {
        if (this.componentData) {
            if (this.componentData.template) {
                this.activeTemplate = this.componentData.template;
            }

            if (this.componentData.formType) {
                this.formType = this.componentData.formType;
            }

            if (this.componentData.selectedState) {
                this._selectedState = this.componentData.selectedState;
                this._originalStateName = this.componentData.selectedState.name;
            }

            if (this.componentData.moduleGroupId) {
                this.moduleGroupId = this.componentData.moduleGroupId;
            }

            if (this.componentData.moduleId) {
                this.moduleId = this.componentData.moduleId;
            }
        }
    }

    selectedStateNameChanged(e: any) {
        let isDuplicate = this.duplicateFormName(e);

        if (!isDuplicate) {
            //hide warning
            this.showDuplicateNameWarning = false;
            //update node in diagram/output to parent
            this.saveMethod.emit({ method: "changeEvent", data: this._selectedState });
            //update previously edited value (change happens while user is typing the name)
            this.preEditStateName = e;
        } else {
            //warn about duplicate name
            this.showDuplicateNameWarning = true;
        }
    }

    duplicateFormName(newStateName: string): boolean {
        for (let s = 1; s < this.activeTemplate.states.length; s++) {
            let st = this.activeTemplate.states[s];
            if (st != this.selectedState && st.name == newStateName) {
                return true;
            }
        }
        return false;
    }

    //close the pane
    closeClick(e: Event) {
        this.sidePanelService.toggleSidePanel(false);
        //are we closing while there's a duplicate name in the textbox?
        if (this.showDuplicateNameWarning) {
            //reset the textbox to previous name
            this.selectedState.name = this.preEditStateName;
            //hide the duplicate warning
            this.showDuplicateNameWarning = false;
        }
        //close goto editor if visible
        this.showGotoEditorUI = false;
    }

    hideFormNameControl(): boolean {
        return this.formType == "start" || this.formType == "end";
    }

    addInputForm(): void {
        this.showTransitionEditor = false;
        this.showGotoEditorUI = false;
        this.requireSave = true;
        this.saveMethod.emit({ method: "addEvent", data: "input" });
    }

    addTransition(): void {
        this.showGotoEditorUI = false;
        this.requireSave = true;
        this.showTransitionEditor = true;
    }

    addTransitionLink(toKey: string): void {
        this.showTransitionEditor = false;
        this.requireSave = true;
        this.saveMethod.emit({ method: "addTransitionEvent", data: { from: this.selectedState.key, to: toKey } });
    }

    addReviewLevel(): void {
        this.requireSave = true;
        this.saveMethod.emit({ method: "addTransitionEvent", data: { from: this.selectedState.key, to: this.selectedState.key } });
    }

    addEndPoint(): void {
        this.showTransitionEditor = false;
        this.showGotoEditorUI = false;
        this.requireSave = true;
        this.saveMethod.emit({ method: "addEvent", data: "end" });
    }

    addReviewForm(): void {
        this.showTransitionEditor = false;
        this.showGotoEditorUI = false;
        this.requireSave = true;
        this.saveMethod.emit({ method: "addEvent", data: "review" });
    }

    //show the 'Goto' link editor
    showGotoEditor(): void {
        this.showTransitionEditor = false;
        this.showGotoEditorUI = true;
    }

    //create link to previous form, aka "redo" this form
    addGotoLink(toKey: string): void {
        this.showGotoEditorUI = false;
        this.requireSave = true;
        this.saveMethod.emit({ method: "addGotoEvent", data: { 'from': this.selectedState.key, 'to': toKey } });
    }

    edit(): void {
        this.flowTemplateService.getByVersion(this.activeTemplate.workflowId, this.activeTemplate.majorVersion, this.activeTemplate.minorVersion, false).pipe(
            first()).subscribe((result) => {
                const savedState = result.states.find(s => s.key === this._selectedState.key);
                if (savedState === undefined || savedState === null) {
                    this.saveTemplate();
                }
                if (savedState !== undefined && savedState !== null) {
                    for (let s = 0; s < this._selectedState.sections.length; s++) {
                        if (this.requireSave) break;
                        const section = this._selectedState.sections[s];
                        const savedSection = savedState.sections.find(sct => sct.name === section.name);
                        if (!savedSection) {
                            this.requireSave = true;
                        } else {
                            for (let p = 0; p < section.pages.length; p++) {
                                const page = section.pages[p];
                                const savedPage = savedSection.pages.find(pg => pg.pageNumber === page.pageNumber);
                                if (savedPage && savedPage.buttons.length !== page.buttons.length) this.requireSave = true;
                            }
                        }
                    }
                }
                this.requireSave = this.requireSave || this.activeTemplate.states.length !== result.states.length;
                if (this.requireSave) {
                    this.saveTemplate();
                } else {
                    this.goToStateBuilder(this.activeTemplate.majorVersion, this.activeTemplate.minorVersion);
                }
            }, (error) => {
                this.saveTemplate();
            });
    }

    goToStateBuilder(majorVersion: number = 0, minorVersion: number = 0): void {
        this.sidePanelService.toggleSidePanel(false);
        const url = (majorVersion === 0 && minorVersion === 0)
            ? `/builder/flow/${this.activeTemplate.workflowId}/states/${this._selectedState.key}`
            : `/builder/flow/${this.activeTemplate.workflowId}/states/${this._selectedState.key}/${majorVersion}/${minorVersion}`;
        this.router.navigate([url]);
    }

    cancel(): void {
        this.selectedState.name = this._originalStateName;
        this.selectedStateNameChanged(this._originalStateName);
        this.closeClick(null);
    }

    saveTemplate(): void {
        //console.log(JSON.stringify(this.activeTemplate));
        const saveService = (this.moduleGroupId !== null && this.moduleId !== null && this.activeTemplate.majorVersion === 0 && this.activeTemplate.minorVersion === 0)
            ? this.flowTemplateService.saveNew(this.moduleGroupId, this.moduleId, this.activeTemplate)
            : this.flowTemplateService.saveAsDraft(this.activeTemplate); //ensures the template is saved and, if necessary, connected to its module before publishing
        saveService.pipe(first()).subscribe((saveResult) => {
            //console.log(saveResult);
            this.activeTemplate.majorVersion = saveResult.majorVersion;
            this.activeTemplate.minorVersion = saveResult.minorVersion;
            this.goToStateBuilder();
        });
    }
}
